import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form } from 'formik';
import { 
    Pane, 
    TextInputField, 
    Button, 
    Heading, 
    IconButton,
    TextInput,
    Select,
    Checkbox,
    Text,
    Badge,
    Card,
    Label,
    ChevronDownIcon,
    ChevronRightIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    TrashIcon
} from 'evergreen-ui';

const CONTENT_URL = process.env.REACT_APP_CONTENT_URL || 'https://content.krit.ro/';

const ITEM_TYPES = [
    { value: 'link', label: 'Link' },
    { value: 'categories', label: 'Categorii (dinamic)' },
];

const HIGHLIGHT_OPTIONS = [
    { value: '', label: 'Fara highlight' },
    { value: 'teal', label: 'Teal' },
    { value: 'orange', label: 'Orange' },
];

// Get icon URL from content server
const getIconUrl = (iconImage) => {
    if (!iconImage) return null;
    if (iconImage.startsWith('data:')) return iconImage; // Base64 preview
    if (iconImage.startsWith('http')) return iconImage;
    return `${CONTENT_URL}public-assets/sidebar/${iconImage}`;
};

// Component to edit a single menu item
const MenuItemEditor = ({ item, onChange, onRemove, onMoveUp, onMoveDown, depth = 0, isNew = false }) => {
    const [expanded, setExpanded] = useState(isNew || !item.label);
    const fileInputRef = useRef(null);
    const isCategories = item.type === 'categories';

    const updateField = (field, value) => {
        onChange({ ...item, [field]: value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const validTypes = ['image/svg+xml', 'image/png'];
            if (!validTypes.includes(file.type)) {
                alert('Doar fisiere SVG sau PNG sunt acceptate');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                updateField('iconImage', reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeIcon = () => {
        updateField('iconImage', null);
    };

    const addSubmenuItem = () => {
        const newSubmenu = [...(item.submenu || []), {
            id: `item-${Date.now()}`,
            label: '',
            type: 'link',
            href: '/'
        }];
        onChange({ ...item, submenu: newSubmenu });
    };

    const updateSubmenuItem = (index, updatedItem) => {
        const newSubmenu = [...item.submenu];
        newSubmenu[index] = updatedItem;
        onChange({ ...item, submenu: newSubmenu });
    };

    const removeSubmenuItem = (index) => {
        const newSubmenu = item.submenu.filter((_, i) => i !== index);
        onChange({ ...item, submenu: newSubmenu });
    };

    const moveSubmenuItem = (index, direction) => {
        const newSubmenu = [...item.submenu];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newSubmenu.length) return;
        [newSubmenu[index], newSubmenu[targetIndex]] = [newSubmenu[targetIndex], newSubmenu[index]];
        onChange({ ...item, submenu: newSubmenu });
    };

    const iconUrl = getIconUrl(item.iconImage);

    return (
        <Card 
            background={depth === 0 ? "tint1" : "tint2"} 
            padding={12} 
            marginBottom={8}
            marginLeft={depth * 16}
            border="default"
        >
            <Pane display="flex" alignItems="center" gap={8}>
                <IconButton 
                    type="button"
                    icon={expanded ? ChevronDownIcon : ChevronRightIcon} 
                    appearance="default"
                    size="small"
                    onClick={() => setExpanded(!expanded)}
                />
                {iconUrl && (
                    <img 
                        src={iconUrl} 
                        alt="" 
                        style={{ width: 20, height: 20, objectFit: 'contain' }} 
                    />
                )}
                <Text flex={1} fontWeight={500} color={item.label ? 'default' : 'muted'}>
                    {item.label || '(element nou - editeaza)'}
                </Text>
                {item.type === 'categories' && <Badge color="blue">Categorii</Badge>}
                {item.type === 'link' && item.href && <Badge color="neutral">{item.href}</Badge>}
                {item.highlight && <Badge color={item.highlight === 'orange' ? 'orange' : 'teal'}>{item.highlight}</Badge>}
                {item.bold && <Badge color="purple">Bold</Badge>}
                <IconButton type="button" icon={ArrowUpIcon} appearance="default" size="small" onClick={onMoveUp} title="Muta sus" />
                <IconButton type="button" icon={ArrowDownIcon} appearance="default" size="small" onClick={onMoveDown} title="Muta jos" />
                <IconButton type="button" icon={TrashIcon} appearance="default" size="small" intent="danger" onClick={onRemove} title="Sterge" />
            </Pane>

            {expanded && (
                <Pane marginTop={12}>
                    {/* Row 1: Name and Type */}
                    <Pane display="flex" gap={12} marginBottom={12}>
                        <Pane flex={2}>
                            <Label display="block" marginBottom={4}>Nume</Label>
                            <TextInput 
                                width="100%"
                                value={item.label || ''}
                                onChange={e => updateField('label', e.target.value)}
                                placeholder="Numele afisat in meniu"
                            />
                        </Pane>
                        <Pane flex={1}>
                            <Label display="block" marginBottom={4}>Tip</Label>
                            <Select 
                                width="100%"
                                value={item.type || 'link'}
                                onChange={e => updateField('type', e.target.value)}
                            >
                                {ITEM_TYPES.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Select>
                        </Pane>
                    </Pane>

                    {/* Row 2: Icon Upload */}
                    <Pane marginBottom={12}>
                        <Label display="block" marginBottom={4}>Icona (SVG sau PNG)</Label>
                        <Pane display="flex" alignItems="center" gap={12}>
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept="image/svg+xml,image/png"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                            <Button 
                                type="button"
                                onClick={() => fileInputRef.current.click()}
                            >
                                {iconUrl ? 'Schimba Icona' : 'Incarca Icona'}
                            </Button>
                            {iconUrl && (
                                <>
                                    <img 
                                        src={iconUrl} 
                                        alt="Icon preview" 
                                        style={{ 
                                            width: 32, 
                                            height: 32, 
                                            objectFit: 'contain',
                                            border: '1px solid #ccc',
                                            borderRadius: 4,
                                            padding: 4
                                        }} 
                                    />
                                    <Button 
                                        type="button"
                                        intent="danger" 
                                        onClick={removeIcon}
                                    >
                                        Sterge
                                    </Button>
                                </>
                            )}
                        </Pane>
                    </Pane>

                    {/* Type-specific fields */}
                    {isCategories ? (
                        <Pane background="blueTint" padding={12} borderRadius={4}>
                            <Text size={300} color="muted" display="block" marginBottom={8}>
                                Configurare Categorii Dinamice
                            </Text>
                            <Pane display="flex" gap={12}>
                                <Pane flex={1}>
                                    <Label display="block" marginBottom={4}>Limita categorii afisate</Label>
                                    <TextInput 
                                        type="number"
                                        width="100%"
                                        value={item.visibleCount || 10}
                                        onChange={e => updateField('visibleCount', parseInt(e.target.value))}
                                    />
                                </Pane>
                                <Pane flex={1}>
                                    <Label display="block" marginBottom={4}>Eticheta "Altele"</Label>
                                    <TextInput 
                                        width="100%"
                                        value={item.othersLabel || 'Altele'}
                                        onChange={e => updateField('othersLabel', e.target.value)}
                                    />
                                </Pane>
                                <Pane display="flex" alignItems="flex-end" paddingBottom={4}>
                                    <Checkbox 
                                        label="Afiseaza Altele"
                                        checked={item.showOthers !== false}
                                        onChange={e => updateField('showOthers', e.target.checked)}
                                    />
                                </Pane>
                            </Pane>
                        </Pane>
                    ) : (
                        <>
                            {/* Link fields */}
                            <Pane display="flex" gap={12} marginBottom={12}>
                                <Pane flex={1}>
                                    <Label display="block" marginBottom={4}>Link (href)</Label>
                                    <TextInput 
                                        width="100%"
                                        value={item.href || ''}
                                        onChange={e => updateField('href', e.target.value)}
                                        placeholder="/pagina sau /categorie/[slug]"
                                    />
                                </Pane>
                                <Pane flex={1}>
                                    <Label display="block" marginBottom={4}>Link afisat (as) - optional</Label>
                                    <TextInput 
                                        width="100%"
                                        value={item.as || ''}
                                        onChange={e => updateField('as', e.target.value)}
                                        placeholder="/categorie/board-games"
                                    />
                                </Pane>
                            </Pane>

                            {/* Additional options */}
                            <Pane display="flex" gap={12} marginBottom={12}>
                                <Pane flex={1}>
                                    <Label display="block" marginBottom={4}>Highlight</Label>
                                    <Select 
                                        width="100%"
                                        value={item.highlight || ''}
                                        onChange={e => updateField('highlight', e.target.value)}
                                    >
                                        {HIGHLIGHT_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </Select>
                                </Pane>
                                <Pane flex={1}>
                                    <Label display="block" marginBottom={4}>Actiune speciala</Label>
                                    <TextInput 
                                        width="100%"
                                        placeholder="ex: logout"
                                        value={item.action || ''}
                                        onChange={e => updateField('action', e.target.value)}
                                    />
                                </Pane>
                                <Pane display="flex" alignItems="flex-end" paddingBottom={4}>
                                    <Checkbox 
                                        label="Text Bold"
                                        checked={item.bold || false}
                                        onChange={e => updateField('bold', e.target.checked)}
                                    />
                                </Pane>
                            </Pane>

                            {/* Submenu */}
                            {depth < 3 && (
                                <Pane marginTop={16} paddingTop={12} borderTop="default">
                                    <Pane display="flex" alignItems="center" marginBottom={8}>
                                        <Text size={400} fontWeight={500}>Submeniu</Text>
                                        <Button 
                                            type="button"
                                            size="small" 
                                            marginLeft={8}
                                            iconBefore="add"
                                            onClick={addSubmenuItem}
                                        >
                                            Adauga
                                        </Button>
                                    </Pane>
                                    {(item.submenu || []).map((subItem, idx) => (
                                        <MenuItemEditor
                                            key={subItem.id || idx}
                                            item={subItem}
                                            onChange={(updated) => updateSubmenuItem(idx, updated)}
                                            onRemove={() => removeSubmenuItem(idx)}
                                            onMoveUp={() => moveSubmenuItem(idx, -1)}
                                            onMoveDown={() => moveSubmenuItem(idx, 1)}
                                            depth={depth + 1}
                                        />
                                    ))}
                                </Pane>
                            )}
                        </>
                    )}
                </Pane>
            )}
        </Card>
    );
};

// Section editor
const SectionEditor = ({ section, onChange, onRemove, onMoveUp, onMoveDown }) => {
    const [expanded, setExpanded] = useState(true);

    const updateField = (field, value) => {
        onChange({ ...section, [field]: value });
    };

    const addItem = () => {
        const items = section.id === 'account' 
            ? (section.authenticated || [])
            : (section.items || []);
        
        const newItem = {
            id: `item-${Date.now()}`,
            label: '',
            type: 'link',
            href: '/'
        };

        if (section.id === 'account') {
            onChange({ 
                ...section, 
                authenticated: [...(section.authenticated || []), newItem],
                unauthenticated: section.unauthenticated || []
            });
        } else {
            onChange({ ...section, items: [...items, newItem] });
        }
    };

    const updateItem = (index, updatedItem, listType = 'items') => {
        const list = section[listType] || [];
        const newList = [...list];
        newList[index] = updatedItem;
        onChange({ ...section, [listType]: newList });
    };

    const removeItem = (index, listType = 'items') => {
        const list = section[listType] || [];
        onChange({ ...section, [listType]: list.filter((_, i) => i !== index) });
    };

    const moveItem = (index, direction, listType = 'items') => {
        const list = [...(section[listType] || [])];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= list.length) return;
        [list[index], list[targetIndex]] = [list[targetIndex], list[index]];
        onChange({ ...section, [listType]: list });
    };

    const isAccountSection = section.id === 'account';

    return (
        <Card background="white" padding={16} marginBottom={16} border="default" elevation={1}>
            <Pane display="flex" alignItems="center" gap={8} marginBottom={expanded ? 16 : 0}>
                <IconButton 
                    type="button"
                    icon={expanded ? ChevronDownIcon : ChevronRightIcon} 
                    appearance="default"
                    size="small"
                    onClick={() => setExpanded(!expanded)}
                />
                <Heading size={500} flex={1}>Sectiune: {section.id}</Heading>
                {section.separator && <Badge>Separator</Badge>}
                {section.position === 'bottom' && <Badge color="blue">Bottom</Badge>}
                <IconButton type="button" icon={ArrowUpIcon} appearance="default" size="small" onClick={onMoveUp} title="Muta sus" />
                <IconButton type="button" icon={ArrowDownIcon} appearance="default" size="small" onClick={onMoveDown} title="Muta jos" />
                <IconButton type="button" icon={TrashIcon} appearance="default" size="small" intent="danger" onClick={onRemove} title="Sterge" />
            </Pane>

            {expanded && (
                <>
                    <Pane display="flex" gap={12} marginBottom={16}>
                        <Pane flex={1}>
                            <Label display="block" marginBottom={4}>ID Sectiune</Label>
                            <TextInput 
                                width="100%"
                                value={section.id || ''}
                                onChange={e => updateField('id', e.target.value)}
                            />
                        </Pane>
                        <Pane display="flex" alignItems="flex-end" gap={16} paddingBottom={4}>
                            <Checkbox 
                                label="Separator deasupra"
                                checked={section.separator || false}
                                onChange={e => updateField('separator', e.target.checked)}
                            />
                            <Checkbox 
                                label="Pozitie bottom"
                                checked={section.position === 'bottom'}
                                onChange={e => updateField('position', e.target.checked ? 'bottom' : undefined)}
                            />
                        </Pane>
                    </Pane>

                    {isAccountSection ? (
                        <>
                            <Pane marginBottom={16}>
                                <Pane display="flex" alignItems="center" marginBottom={8}>
                                    <Text size={400} fontWeight={500} color="green">Autentificat</Text>
                                    <Button type="button" size="small" marginLeft={8} iconBefore="add" onClick={() => {
                                        const newItem = { id: `item-${Date.now()}`, label: '', type: 'link', href: '/' };
                                        onChange({ ...section, authenticated: [...(section.authenticated || []), newItem] });
                                    }}>
                                        Adauga
                                    </Button>
                                </Pane>
                                {(section.authenticated || []).map((item, idx) => (
                                    <MenuItemEditor
                                        key={item.id || idx}
                                        item={item}
                                        onChange={(updated) => updateItem(idx, updated, 'authenticated')}
                                        onRemove={() => removeItem(idx, 'authenticated')}
                                        onMoveUp={() => moveItem(idx, -1, 'authenticated')}
                                        onMoveDown={() => moveItem(idx, 1, 'authenticated')}
                                    />
                                ))}
                            </Pane>
                            <Pane>
                                <Pane display="flex" alignItems="center" marginBottom={8}>
                                    <Text size={400} fontWeight={500} color="orange">Neautentificat</Text>
                                    <Button type="button" size="small" marginLeft={8} iconBefore="add" onClick={() => {
                                        const newItem = { id: `item-${Date.now()}`, label: '', type: 'link', href: '/' };
                                        onChange({ ...section, unauthenticated: [...(section.unauthenticated || []), newItem] });
                                    }}>
                                        Adauga
                                    </Button>
                                </Pane>
                                {(section.unauthenticated || []).map((item, idx) => (
                                    <MenuItemEditor
                                        key={item.id || idx}
                                        item={item}
                                        onChange={(updated) => updateItem(idx, updated, 'unauthenticated')}
                                        onRemove={() => removeItem(idx, 'unauthenticated')}
                                        onMoveUp={() => moveItem(idx, -1, 'unauthenticated')}
                                        onMoveDown={() => moveItem(idx, 1, 'unauthenticated')}
                                    />
                                ))}
                            </Pane>
                        </>
                    ) : (
                        <Pane>
                            <Pane display="flex" alignItems="center" marginBottom={8}>
                                <Text size={400} fontWeight={500}>Elemente</Text>
                                <Button type="button" size="small" marginLeft={8} iconBefore="add" onClick={addItem}>
                                    Adauga Element
                                </Button>
                            </Pane>
                            {(section.items || []).map((item, idx) => (
                                <MenuItemEditor
                                    key={item.id || idx}
                                    item={item}
                                    onChange={(updated) => updateItem(idx, updated)}
                                    onRemove={() => removeItem(idx)}
                                    onMoveUp={() => moveItem(idx, -1)}
                                    onMoveDown={() => moveItem(idx, 1)}
                                />
                            ))}
                        </Pane>
                    )}
                </>
            )}
        </Card>
    );
};

const SidebarForm = ({ isEdit, initialValues, validationSchema, onSave, errors }) => {
    const [definition, setDefinition] = useState(initialValues?.definition || { sections: [] });

    useEffect(() => {
        if (initialValues?.definition) {
            // Ensure definition has sections array
            const def = initialValues.definition;
            setDefinition({
                ...def,
                sections: def.sections || []
            });
        }
    }, [initialValues]);

    const addSection = () => {
        setDefinition({
            ...definition,
            sections: [...definition.sections, {
                id: `section-${Date.now()}`,
                items: []
            }]
        });
    };

    const updateSection = (index, updatedSection) => {
        const newSections = [...definition.sections];
        newSections[index] = updatedSection;
        setDefinition({ ...definition, sections: newSections });
    };

    const removeSection = (index) => {
        setDefinition({
            ...definition,
            sections: definition.sections.filter((_, i) => i !== index)
        });
    };

    const moveSection = (index, direction) => {
        const newSections = [...definition.sections];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newSections.length) return;
        [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
        setDefinition({ ...definition, sections: newSections });
    };

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues || { name: '', definition: { sections: [] } }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
                onSave({
                    ...values,
                    definition
                });
            }}
        >
            {({ values, handleChange, handleSubmit }) => (
                <Form onSubmit={handleSubmit}>
                    <Pane marginBottom={24}>
                        <TextInputField
                            name="name"
                            label="Nume Sidebar"
                            value={values.name || ''}
                            onChange={handleChange}
                            required
                        />
                    </Pane>

                    <Pane marginBottom={16}>
                        <Pane display="flex" alignItems="center" marginBottom={16}>
                            <Heading size={600}>Sectiuni Meniu</Heading>
                            <Button 
                                type="button"
                                marginLeft={16}
                                iconBefore="add"
                                onClick={addSection}
                            >
                                Adauga Sectiune
                            </Button>
                        </Pane>

                        {(definition.sections || []).map((section, idx) => (
                            <SectionEditor
                                key={section.id || idx}
                                section={section}
                                onChange={(updated) => updateSection(idx, updated)}
                                onRemove={() => removeSection(idx)}
                                onMoveUp={() => moveSection(idx, -1)}
                                onMoveDown={() => moveSection(idx, 1)}
                            />
                        ))}
                    </Pane>

                    <Pane marginTop={24}>
                        <Button type="submit" appearance="primary" iconBefore="floppy-disk">
                            {isEdit ? 'Salveaza Modificarile' : 'Creeaza Sidebar'}
                        </Button>
                    </Pane>
                </Form>
            )}
        </Formik>
    );
};

export default SidebarForm;
